import React from 'react';
import * as Styled from './styled';
import { shouldLazilyLoad, getInfinitePrevIndex, getInfiniteNextIndex } from 'utils/gallery';
import { IThumbnail } from 'utils/entities';
import Share from 'components/navigation/share';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import CloseIcon from 'assets/svg/close.svg';
import { LAZY_LOAD_MARGIN } from '../utils';
import ControlsIcon from 'assets/svg/control-chevron.svg';
import ImageGalleryItem from '../screen-item';
import { useLocale } from 'locale';
import ConnectedGalleryStickyContactEntry from 'components/contact/gallery-sticky';
import { ViewPager } from 'components/view-pager';
import { SafeSuspense } from 'utils/SafeSuspense';

interface EventViewPhotoScrollProps {
  dir: string;
  image: IThumbnail;
  index: number;
  total: number;
}

export interface CarouselProps {
  images: IThumbnail[];
  id: string;
  copyText?: string;
  initialIndex: number;
  onClose: () => void;
  eventViewPhotoScroll?: (opts: EventViewPhotoScrollProps) => void;
}


export const Carousel: React.FC<CarouselProps> = (props) => {
  const {
    eventViewPhotoScroll,
    initialIndex,
    copyText,
    onClose,
    images,
    id,
  } = props;

  const total = images.length;
  const { t } = useLocale();
  const [ slideIndex, setSlideIndex ] = React.useState(initialIndex);

  const disabledNext = slideIndex === (total - 1);
  const disabledPrev = slideIndex === 0;

  const handleSlideIndexChange = (nextIdx: number) => {
    const image = images[nextIdx];
    let dir = 'left';
    if (nextIdx === 0 && slideIndex === images.length - 1) {
      dir = 'right';
    }
    else if (nextIdx === images.length - 1 && slideIndex === 0) {
      dir = 'left';
    }
    else if (slideIndex < nextIdx) {
      dir = 'right';
    }
    if (eventViewPhotoScroll) {
      eventViewPhotoScroll({ dir, image, index: nextIdx, total });
    }
    setSlideIndex(nextIdx);
  };

  const handlePrevSlide = () => {
    if (disabledPrev) return;

    const nextIndex = getInfinitePrevIndex(slideIndex, total);
    handleSlideIndexChange(nextIndex);
  };

  const handleNextSlide = () => {
    if (disabledNext) return;

    const nextIndex = getInfiniteNextIndex(slideIndex, total);
    handleSlideIndexChange(nextIndex);
  };


  return (
    <Styled.Root>
      <Styled.Header>
        <Styled.BackBtn onClick={onClose}>
          <CloseIcon width={24} height={24} />
        </Styled.BackBtn>
        <Styled.ExtraBtnWrapper>
          <Share tooltip={false} copyText={copyText} menuZIndex={9999} alignment="right" isRound />
          <FavoritesToggler id={id} type="bulletin" tooltip={false} withButton noText isRound />
        </Styled.ExtraBtnWrapper>
      </Styled.Header>
      <Styled.SliderPagerBody>
        <Styled.SliderPager>
          <SafeSuspense>
            <ViewPager slideIndex={slideIndex} onSlideIndexChange={handleSlideIndexChange}>
              {images.map((thumbnail, i) => (
                <ImageGalleryItem
                  key={thumbnail.url}
                  useWidth
                  shouldLoad={shouldLazilyLoad(slideIndex, i, images.length, LAZY_LOAD_MARGIN)}
                  thumbnail={thumbnail}
                />
              ))}
            </ViewPager>
          </SafeSuspense>
        </Styled.SliderPager>
        <Styled.SliderPagerControlsBox>
          <Styled.SliderPagerControl disabled={disabledPrev} dir="prev" onClick={handlePrevSlide}>
            <ControlsIcon height={20} />
          </Styled.SliderPagerControl>
          <Styled.PageInfo weight="bold">
            {t('unitPage.carousel.gallery.page.info', { current: slideIndex + 1, total })}
          </Styled.PageInfo>
          <Styled.SliderPagerControl disabled={disabledNext} dir="next" onClick={handleNextSlide}>
            <ControlsIcon height={20} />
          </Styled.SliderPagerControl>
        </Styled.SliderPagerControlsBox>
      </Styled.SliderPagerBody>
      <Styled.Footer>
        <ConnectedGalleryStickyContactEntry id={id} />
      </Styled.Footer>
    </Styled.Root>
  );
};
